import React from 'react';
import {useForm, Controller} from 'react-hook-form';
import {useSelector, useDispatch} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {selectAgencyData} from 'src/selectors/agency';
import {getAccounts} from 'src/action-creators/accounts';
import type {State} from 'src/reducers';
import {AnalyticsService} from 'src/analytics';
import {ANALYTICS_SCHEDULAR_PANEL_EVENTS} from 'src/components/analytics-genesis/tracking-events';
import {
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  CollapsibleCard,
  CollapsibleCardContent,
} from '@spaced-out/ui-design-system/lib/components/CollapsibleCard';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  FormLabelSmall,
  SubTitleLarge,
  TEXT_COLORS,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';
import {Typeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {useFilteredOptions} from '@spaced-out/ui-design-system/lib/hooks/useFilteredOptions';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';

import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';
import type {
  SchedularPanelFormData,
  SchedularPanelFrequency,
  SchedularPanelTimePeriod,
  SchedularPanelMode,
} from 'src/types/analytics-genesis';
import {
  validateEmail,
  generateMonthDayOptions,
  generateTimeOptions,
  getSchedularPanelFrequencyOptions,
  getSchedularPanelDayOptions,
  mapDayOfWeekToString,
  mapDayOfWeek,
} from 'src/utils/analytics-genesis';

import css from './schedular-panel.css';


const getDefaultFormData = (options: {
  product: string;
  dashboard: string;
  path: string[];
  agencyTimezone: string;
  reportName?: string;
}): SchedularPanelFormData => ({
  product: options.product,
  dashboard: options.dashboard,
  path: options.path,
  schedule: {
    frequency: 'daily',
    time: '09:00',
    day_of_week: null,
    day_of_month: null,
    special_day: null,
  },
  filters: {},
  time_window: 'Last 7 days',
  agency_tz: options.agencyTimezone,
  report_payload: {
    report_name: options.reportName || '',
    emails: [],
    subject: '',
  },
});

export type SchedularPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  mode?: SchedularPanelMode;
  isLoading?: boolean;
  reportName?: string;
  product?: string;
  dashboard?: string;
  path?: Array<string>;
  onSave?: (data: SchedularPanelFormData) => Promise<void> | void;
  onCancel?: () => void;
  onTestSend?: () => void;
  initialData?: Partial<SchedularPanelFormData>;
  /**
   * Optional delete affordance. When provided, a destructive "Delete schedule"
   * button renders in the footer in edit mode. Omit it to keep the original
   * two-button (Cancel / Save) footer for callers that manage deletion
   * elsewhere or don't support it.
   */
  onDelete?: () => Promise<void> | void;
  /** Loading state for the delete button (mirrors `isLoading` for save). */
  isDeleting?: boolean;
  /**
   * Hide the monthly "last day of month" option. Set by callers whose backend
   * can't represent it (e.g. BYOD caps `day_of_month` at 28), so users aren't
   * offered a choice that would be silently downgraded to the 28th.
   */
  hideLastDayOfMonth?: boolean;
};

export const SchedularPanel = ({
  isOpen,
  onClose,
  mode = 'create',
  isLoading = false,
  reportName: propReportName = '',
  product = 'Workflows',
  dashboard = 'E and D',
  path = [],
  onSave,
  onCancel,
  initialData,
  onDelete,
  isDeleting = false,
  hideLastDayOfMonth = false,
}: SchedularPanelProps) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const {
    id: agencyId,
    name: agencyName,
    timezone: agencyTimezone = 'UTC',
  } = useSelector(selectAgencyData);

  const getInitialFormData = React.useCallback((): SchedularPanelFormData => {
    return getDefaultFormData({
      product,
      dashboard,
      path,
      agencyTimezone,
      reportName: propReportName,
    });
  }, [product, dashboard, path, agencyTimezone, propReportName]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: {errors},
  } = useForm<SchedularPanelFormData>({
    defaultValues: getInitialFormData(),
    mode: 'onChange',
  });

  const [isSaving, setIsSaving] = React.useState(false);

  const isDisabled = mode === 'view' || isLoading || isSaving;

  // Watch form values for conditional rendering and display
  const formData = watch();
  const selectedFrequency = formData.schedule.frequency;
  const scheduleDay =
    formData.schedule.day_of_week !== null
      ? mapDayOfWeekToString(formData.schedule.day_of_week)
      : formData.schedule.day_of_month !== null
      ? formData.schedule.day_of_month.toString()
      : formData.schedule.special_day === 'last_day'
      ? 'last'
      : undefined;
  const selectedWeekdays = React.useMemo(
    () => (scheduleDay ? [scheduleDay] : []),
    [scheduleDay],
  );
  const selectedMonthDay = scheduleDay;

  const emailRecipients = React.useMemo<Array<MenuOption>>(
    () =>
      (formData.report_payload.emails || []).map((email) => ({
        key: email,
        label: email,
      })),
    [formData.report_payload.emails],
  );

  // Initialize form with initialData when available
  React.useEffect(() => {
    if (!isLoading && initialData) {
      const baseData = getInitialFormData();
      reset({
        ...baseData,
        ...initialData,
      });
    }
  }, [isLoading, initialData, getInitialFormData, reset]);

  // Track panel open/close and reset form when panel closes
  React.useEffect(() => {
    if (isOpen) {
      // Track panel opened
      AnalyticsService.track(ANALYTICS_SCHEDULAR_PANEL_EVENTS.PANEL_OPENED, {
        'Agency Id': agencyId,
        'Agency Name': agencyName,
        Mode: mode,
        Product: product,
        Dashboard: dashboard,
        'Report Name': propReportName,
      });
    } else {
      // Track panel closed
      AnalyticsService.track(ANALYTICS_SCHEDULAR_PANEL_EVENTS.PANEL_CLOSED, {
        'Agency Id': agencyId,
        'Agency Name': agencyName,
        Mode: mode,
        Product: product,
        Dashboard: dashboard,
      });
      reset(getInitialFormData());
    }
  }, [
    isOpen,
    getInitialFormData,
    reset,
    agencyId,
    agencyName,
    mode,
    product,
    dashboard,
    propReportName,
  ]);

  const [emailInputText, setEmailInputText] = React.useState('');

  const accounts = useSelector((state: State) => state.accounts.data);

  const isAccountsLoading = useAsyncDependency(
    () => dispatch(getAccounts()),
    [],
  );

  const accountEmailOptions = React.useMemo<Array<MenuOption>>(() => {
    const accountsArray = Object.values(accounts);

    return accountsArray
      .filter((account) => account.email && !account.suspended)
      .map((account) => {
        const {name, email} = account;
        let label = email;
        if (name) {
          label = `${name} <${email}>`;
        }
        return {
          key: email,
          label,
        };
      });
  }, [accounts]);

  const frequencyOptions = React.useMemo(
    () => getSchedularPanelFrequencyOptions(t),
    [t],
  );

  const dayOptions = React.useMemo(() => getSchedularPanelDayOptions(t), [t]);

  const monthDayOptions = React.useMemo(
    () => generateMonthDayOptions(t, {includeLastDay: !hideLastDayOfMonth}),
    [t, hideLastDayOfMonth],
  );

  const timeOptions = React.useMemo(() => generateTimeOptions(), []);

  const timeWindowOptions = React.useMemo(() => {
    return [
      {
        key: 'Current Year',
        label: t('Current_Year', 'Current Year'),
      },
      {
        key: 'Last day',
        label: t('Last_day', 'Last day'),
      },
      {
        key: 'Last 7 days',
        label: t('Last_7_days', 'Last 7 days'),
      },
      {
        key: 'Last 30 days',
        label: t('Last_30_days', 'Last 30 days'),
      },
      {
        key: 'Last 90 days',
        label: t('Last_90_days', 'Last 90 days'),
      },
    ];
  }, [t]);

  const {filteredOptions: filteredEmailOptions} = useFilteredOptions({
    searchTerm: emailInputText,
    options: accountEmailOptions,
    excludedKeys: emailRecipients.map((value) => value.key),
  });

  const handleEmailSearch = React.useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setEmailInputText(evt.target.value);
    },
    [],
  );

  const handleEmailSelect = React.useCallback(
    (option: MenuOption, setValue: (emails: string[]) => void) => {
      const selectedEmails = formData.report_payload.emails || [];
      if (!selectedEmails.includes(option.key)) {
        setValue([...selectedEmails, option.key]);

        // Track email added
        AnalyticsService.track(ANALYTICS_SCHEDULAR_PANEL_EVENTS.EMAIL_ADDED, {
          'Agency Id': agencyId,
          'Agency Name': agencyName,
          Mode: mode,
          Product: product,
          Dashboard: dashboard,
          'Total Recipients': selectedEmails.length + 1,
        });
      }
      setEmailInputText('');
    },
    [
      formData.report_payload.emails,
      agencyId,
      agencyName,
      mode,
      product,
      dashboard,
    ],
  );

  const handleEmailDismiss = React.useCallback(
    (email: string, setValue: (emails: string[]) => void) => {
      const selectedEmails = formData.report_payload.emails || [];
      setValue(selectedEmails.filter((e) => e !== email));

      // Track email removed
      AnalyticsService.track(ANALYTICS_SCHEDULAR_PANEL_EVENTS.EMAIL_REMOVED, {
        'Agency Id': agencyId,
        'Agency Name': agencyName,
        Mode: mode,
        Product: product,
        Dashboard: dashboard,
        'Total Recipients': selectedEmails.length - 1,
      });
    },
    [
      formData.report_payload.emails,
      agencyId,
      agencyName,
      mode,
      product,
      dashboard,
    ],
  );

  const handleEmailClear = React.useCallback(() => {
    setEmailInputText('');
  }, []);

  const onSubmit = async (data: SchedularPanelFormData) => {
    if (!onSave) {
      return;
    }

    const finalFormData: SchedularPanelFormData = {
      ...data,
      report_payload: {
        ...data.report_payload,
        report_name: data.report_payload.report_name || propReportName,
      },
    };

    try {
      setIsSaving(true);
      await onSave(finalFormData);

      // Track schedule created/updated
      const eventName =
        mode === 'edit'
          ? ANALYTICS_SCHEDULAR_PANEL_EVENTS.SCHEDULE_UPDATED
          : ANALYTICS_SCHEDULAR_PANEL_EVENTS.SCHEDULE_CREATED;

      AnalyticsService.track(eventName, {
        'Agency Id': agencyId,
        'Agency Name': agencyName,
        Mode: mode,
        Product: product,
        Dashboard: dashboard,
        'Report Name': finalFormData.report_payload.report_name,
        Frequency: finalFormData.schedule.frequency,
        Time: finalFormData.schedule.time,
        'Time Window': finalFormData.time_window,
        'Number of Recipients':
          finalFormData.report_payload.emails?.length || 0,
      });
    } catch {
      // Error already handled by onSave
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Track schedule cancelled
    AnalyticsService.track(
      ANALYTICS_SCHEDULAR_PANEL_EVENTS.SCHEDULE_CANCELLED,
      {
        'Agency Id': agencyId,
        'Agency Name': agencyName,
        Mode: mode,
        Product: product,
        Dashboard: dashboard,
      },
    );

    onCancel?.();
    onClose();
  };

  const getHeaderTitle = (): string => {
    if (isLoading || isAccountsLoading) {
      return t('Loading', 'Loading...');
    }
    const reportName = watch('report_payload.report_name');
    if (mode === 'view' && reportName) {
      return reportName;
    }
    if (mode === 'edit' && reportName) {
      return reportName;
    }
    if (mode === 'create') {
      return t('Schedule_Report', 'Schedule Report');
    }
    return t('Schedule_Report', 'Schedule Report');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Panel
      isOpen={isOpen}
      anchor="right"
      size="large"
      onClose={onClose}
      tapOutsideToClose={!isDisabled}
      hideBackdrop={false}
      testId={generateTestId({base: 'schedular-panel'})}
    >
      <PanelHeader
        onCloseButtonClick={onClose}
        size="medium"
        testId={generateTestId({base: 'schedular-panel-header'})}
      >
        <SubTitleLarge>{getHeaderTitle()}</SubTitleLarge>
      </PanelHeader>

      <PanelBody className={css.panelBody}>
        {isLoading || isAccountsLoading ? (
          <div className={css.loadingContainer}>
            <CircularLoader
              colorToken="colorFillPrimary"
              size="large"
              testId={generateTestId({base: 'schedular-panel-loader'})}
            />
          </div>
        ) : (
          <>
            <CollapsibleCard
              title={t('Reports_and_Filters', 'Reports and Filters')}
              headerIconName="chart-mixed"
              isOpen={true}
              testId={generateTestId({
                base: 'schedular-panel-reports-filters-card',
              })}
            >
              <CollapsibleCardContent className={css.reportFilterContainer}>
                <FormLabelSmall color={TEXT_COLORS.secondary}>
                  {t('Data', 'Data')}
                </FormLabelSmall>
                <div className={css.reportPathContainer}>
                  <Chip>{product}</Chip>
                  <Icon name="chevron-right" />
                  <Chip>{dashboard}</Chip>
                  {path.map((item) => {
                    const itemKey = `path-${item}`;
                    return (
                      <React.Fragment key={itemKey}>
                        <Icon name="chevron-right" />
                        <Chip>{item}</Chip>
                      </React.Fragment>
                    );
                  })}
                </div>
              </CollapsibleCardContent>
            </CollapsibleCard>

            <CollapsibleCard
              title={t('Schedule_and_Conditions', 'Schedule and Conditions')}
              headerIconName="clock"
              isOpen={true}
              testId={generateTestId({
                base: 'schedular-panel-schedule-conditions-card',
              })}
            >
              <CollapsibleCardContent className={css.scheduleContainer}>
                <Controller
                  name="schedule.frequency"
                  control={control}
                  render={({field}) => (
                    <SimpleDropdown
                      label={t('Frequency', 'Frequency')}
                      classNames={{
                        wrapper: css.dropdownRow,
                      }}
                      options={frequencyOptions}
                      selectedKeys={[field.value]}
                      onChange={(option) => {
                        if (!isDisabled) {
                          const newFrequency =
                            option.key as SchedularPanelFrequency;
                          field.onChange(newFrequency);
                          setValue('schedule.day_of_week', null);
                          setValue('schedule.day_of_month', null);
                          setValue('schedule.special_day', null);

                          // Track frequency change
                          AnalyticsService.track(
                            ANALYTICS_SCHEDULAR_PANEL_EVENTS.FREQUENCY_CHANGED,
                            {
                              'Agency Id': agencyId,
                              'Agency Name': agencyName,
                              Mode: mode,
                              Product: product,
                              Dashboard: dashboard,
                              'Previous Frequency': field.value,
                              'New Frequency': newFrequency,
                            },
                          );
                        }
                      }}
                      size="medium"
                      testId={generateTestId({
                        base: 'schedular-panel-frequency-dropdown',
                      })}
                      disabled={isDisabled}
                    />
                  )}
                />

                {selectedFrequency !== 'daily' && (
                  <Controller
                    name={
                      selectedFrequency === 'monthly'
                        ? 'schedule.day_of_month'
                        : 'schedule.day_of_week'
                    }
                    control={control}
                    rules={{
                      validate: () => {
                        const currentFormData = watch();
                        if (currentFormData.schedule.frequency === 'weekly') {
                          if (currentFormData.schedule.day_of_week === null) {
                            return t(
                              'Please_select_a_day_of_the_week',
                              'Please select a day of the week',
                            );
                          }
                        } else if (
                          currentFormData.schedule.frequency === 'monthly'
                        ) {
                          if (
                            currentFormData.schedule.day_of_month === null &&
                            currentFormData.schedule.special_day === null
                          ) {
                            return t(
                              'Please_select_a_day_of_the_month',
                              'Please select a day of the month',
                            );
                          }
                        }
                        return true;
                      },
                    }}
                    render={({fieldState}) => {
                      const handleChange = (option: {key: string}) => {
                        if (!isDisabled) {
                          if (selectedFrequency === 'monthly') {
                            if (option.key === 'last') {
                              setValue('schedule.special_day', 'last_day', {
                                shouldValidate: true,
                              });
                              setValue('schedule.day_of_month', null, {
                                shouldValidate: true,
                              });
                            } else {
                              setValue(
                                'schedule.day_of_month',
                                parseInt(option.key, 10),
                                {shouldValidate: true},
                              );
                              setValue('schedule.special_day', null, {
                                shouldValidate: true,
                              });
                            }
                          } else {
                            const dayOfWeek = mapDayOfWeek(option.key);
                            setValue('schedule.day_of_week', dayOfWeek, {
                              shouldValidate: true,
                            });
                          }

                          // Track day selection
                          AnalyticsService.track(
                            ANALYTICS_SCHEDULAR_PANEL_EVENTS.DAY_SELECTED,
                            {
                              'Agency Id': agencyId,
                              'Agency Name': agencyName,
                              Mode: mode,
                              Product: product,
                              Dashboard: dashboard,
                              Frequency: selectedFrequency,
                              'Selected Day': option.key,
                            },
                          );
                        }
                      };

                      return (
                        <>
                          <SimpleDropdown
                            label={t('Select_Day', 'Select Day')}
                            options={
                              selectedFrequency === 'monthly'
                                ? monthDayOptions
                                : dayOptions
                            }
                            classNames={{
                              wrapper: css.dropdownRow,
                            }}
                            selectedKeys={
                              selectedFrequency === 'monthly'
                                ? selectedMonthDay
                                  ? [selectedMonthDay]
                                  : []
                                : selectedWeekdays
                            }
                            onChange={handleChange}
                            size="medium"
                            testId={generateTestId({
                              base: 'schedular-panel-day-selection-dropdown',
                            })}
                            disabled={isDisabled}
                            errorText={fieldState.error?.message as string}
                            error={!!fieldState.error}
                          />
                        </>
                      );
                    }}
                  />
                )}

                <Controller
                  name="schedule.time"
                  control={control}
                  render={({field}) => (
                    <SimpleDropdown
                      label={t('Select_Time', 'Select Time')}
                      options={timeOptions}
                      classNames={{
                        wrapper: css.dropdownRow,
                      }}
                      selectedKeys={[field.value]}
                      onChange={(option) => {
                        if (!isDisabled) {
                          const newTime = option.key;
                          field.onChange(newTime);

                          // Track time selection
                          AnalyticsService.track(
                            ANALYTICS_SCHEDULAR_PANEL_EVENTS.TIME_SELECTED,
                            {
                              'Agency Id': agencyId,
                              'Agency Name': agencyName,
                              Mode: mode,
                              Product: product,
                              Dashboard: dashboard,
                              'Selected Time': newTime,
                            },
                          );
                        }
                      }}
                      size="medium"
                      testId={generateTestId({
                        base: 'schedular-panel-time-selection-dropdown',
                      })}
                      disabled={isDisabled}
                      error={!!errors.schedule?.time}
                      errorText={errors.schedule?.time?.message as string}
                    />
                  )}
                />

                <Controller
                  name="time_window"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!value) {
                        return t(
                          'Date_Range_is_required',
                          'Date Range is required',
                        );
                      }
                      return true;
                    },
                  }}
                  render={({field}) => (
                    <>
                      <SimpleDropdown
                        label={t('Date_Range', 'Date Range')}
                        classNames={{
                          wrapper: css.dropdownRow,
                        }}
                        options={timeWindowOptions}
                        selectedKeys={
                          field.value ? [field.value as string] : []
                        }
                        onChange={(option) => {
                          if (!isDisabled) {
                            const newDateRange =
                              option.key as SchedularPanelTimePeriod;
                            field.onChange(newDateRange);

                            // Track date range selection
                            AnalyticsService.track(
                              ANALYTICS_SCHEDULAR_PANEL_EVENTS.DATE_RANGE_SELECTED,
                              {
                                'Agency Id': agencyId,
                                'Agency Name': agencyName,
                                Mode: mode,
                                Product: product,
                                Dashboard: dashboard,
                                'Selected Date Range': newDateRange,
                              },
                            );
                          }
                        }}
                        size="medium"
                        testId={generateTestId({
                          base: 'schedular-panel-time-window-dropdown',
                        })}
                        disabled={isDisabled}
                        error={!!errors.time_window}
                        errorText={errors.time_window?.message as string}
                      />
                    </>
                  )}
                />
              </CollapsibleCardContent>
            </CollapsibleCard>

            <CollapsibleCard
              title={t(
                'Recipients_and_Email_Configuration',
                'Recipients and Email Configuration',
              )}
              headerIconName="envelope"
              isOpen={true}
              testId={generateTestId({
                base: 'schedular-panel-recipients-email-card',
              })}
            >
              <CollapsibleCardContent
                className={css.recipientsAndEmailConfigurationContainer}
              >
                <Controller
                  name="report_payload.report_name"
                  control={control}
                  rules={{
                    required: t(
                      'Report_Name_is_required',
                      'Report Name is required',
                    ),
                  }}
                  render={({field}) => (
                    <Input
                      label={t('Report_Name', 'Report Name')}
                      required={true}
                      value={field.value}
                      placeholder={t('Enter_report_name', 'Enter report name')}
                      onChange={(e) => {
                        if (!isDisabled) {
                          field.onChange(e.target.value);
                        }
                      }}
                      error={!!errors.report_payload?.report_name}
                      errorText={
                        errors.report_payload?.report_name?.message as string
                      }
                      classNames={{
                        wrapper: css.inputRow,
                      }}
                      disabled={isDisabled}
                      testId={generateTestId({
                        base: 'schedular-panel-report-name-input',
                      })}
                    />
                  )}
                />

                <div className={css.emailRecipientsContainer}>
                  <FormLabelSmall color={TEXT_COLORS.secondary}>
                    {t('Send_to', 'Send to')}
                  </FormLabelSmall>
                  <Controller
                    name="report_payload.emails"
                    control={control}
                    rules={{
                      required: t(
                        'At_least_one_email_is_required',
                        'At least one email is required',
                      ),
                      validate: (value) => {
                        if (!value || value.length === 0) {
                          return t(
                            'At_least_one_email_is_required',
                            'At least one email is required',
                          );
                        }
                        const invalidEmails = value.filter(
                          (email) => !validateEmail(email),
                        );
                        if (invalidEmails.length > 0) {
                          return t(
                            'Please_enter_valid_email_addresses',
                            'Please enter valid email addresses',
                          );
                        }
                        return true;
                      },
                    }}
                    render={({field, fieldState}) => (
                      <>
                        <Typeahead
                          label={t(
                            'Enter_email_IDs_to_send_report_to',
                            'Enter email IDs to send report to',
                          )}
                          size="small"
                          placeholder={`${t('Search', 'Search')} ${t(
                            'Enter_email_IDs_to_send_report_to',
                            'Enter email IDs to send report to',
                          )}`}
                          typeaheadInputText={emailInputText}
                          menu={{
                            options: filteredEmailOptions,
                            selectedKeys: emailRecipients.map(({key}) => key),
                            allowWrap: true,
                            staticLabels: {
                              SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
                              RESULTS: t('results', 'results'),
                              RESULT: t('result', 'result'),
                            },
                            virtualization: {
                              enable: true,
                              menuHeight: 300,
                              itemHeight: 30,
                            },
                          }}
                          onSearch={handleEmailSearch}
                          isLoading={isAccountsLoading}
                          onClear={handleEmailClear}
                          onSelect={(option) => {
                            if (!isDisabled) {
                              handleEmailSelect(option, field.onChange);
                            }
                          }}
                          menuOpenOffset={0}
                          error={!!fieldState.error}
                          errorText={fieldState.error?.message as string}
                          disabled={isDisabled}
                          testId={generateTestId({
                            base: 'schedular-panel-email-recipients-input',
                          })}
                          classNames={{box: css.emailRecipientsInput}}
                        />
                        {Boolean(emailRecipients.length) && (
                          <>
                            <div className={css.chipContainer}>
                              {emailRecipients.map(({key, label}) => {
                                return (
                                  <Chip
                                    key={key}
                                    dismissable
                                    onDismiss={() => {
                                      if (!isDisabled) {
                                        handleEmailDismiss(key, field.onChange);
                                      }
                                    }}
                                  >
                                    <TruncatedTextWithTooltip
                                      tooltip={{bodyMaxLines: 10}}
                                    >
                                      {label}
                                    </TruncatedTextWithTooltip>
                                  </Chip>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  />
                </div>

                <Controller
                  name="report_payload.subject"
                  control={control}
                  rules={{
                    required: t(
                      'Email_Subject_is_required',
                      'Email Subject is required',
                    ),
                  }}
                  render={({field}) => (
                    <Textarea
                      label={t('Email_Subject', 'Email Subject')}
                      required={true}
                      value={field.value}
                      placeholder={t(
                        'Enter_email_subject',
                        'Enter email subject',
                      )}
                      onChange={(e) => {
                        if (!isDisabled) {
                          field.onChange(e.target.value);
                        }
                      }}
                      error={!!errors.report_payload?.subject}
                      errorText={
                        errors.report_payload?.subject?.message as string
                      }
                      classNames={{box: css.textarea}}
                      disabled={isDisabled}
                      testId={generateTestId({
                        base: 'schedular-panel-email-subject-textarea',
                      })}
                    />
                  )}
                />
              </CollapsibleCardContent>
            </CollapsibleCard>
          </>
        )}
      </PanelBody>
      {mode !== 'view' && (
        <PanelFooter>
          {mode === 'edit' && onDelete && (
            <Button
              type="danger"
              iconLeftName="trash"
              onClick={() => {
                void onDelete();
              }}
              disabled={isLoading || isSaving || isDeleting}
              isLoading={isDeleting}
              testId={generateTestId({base: 'schedular-panel-delete-button'})}
            >
              {t('Delete_schedule', 'Delete schedule')}
            </Button>
          )}
          <Button
            type="ghost"
            onClick={handleCancel}
            disabled={isLoading || isSaving || isDeleting}
            testId={generateTestId({base: 'schedular-panel-cancel-button'})}
          >
            {t('Cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || isSaving || isDeleting}
            isLoading={isSaving}
            testId={generateTestId({
              base: 'schedular-panel-save-button',
              slot: mode,
            })}
          >
            {mode === 'edit'
              ? t('Update', 'Update')
              : t('Add_Alert', 'Add Alert')}
          </Button>
        </PanelFooter>
      )}
    </Panel>
  );
};
